import React, { useEffect, useState } from "react";
import { Save, Plus, Trash2, Loader2 } from "lucide-react";
import { Panel, MonoLabel } from "@/components/Panel";
import { toast } from "sonner";
import { api, errText } from "@/lib/devadminApi";

const NEW = () => ({ id: `new-${Date.now()}`, _new: true, name: "New window", start: "12:00", end: "13:00", grace_min: 15, tiers: [] });

export function Meals({ eventId }) {
  const [rows, setRows] = useState(null);
  const [orig, setOrig] = useState({});
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/events/${eventId}/meal-windows`).then((r) => {
    setRows(r.data); setOrig(Object.fromEntries(r.data.map((x) => [x.id, x])));
  }).catch((e) => toast.error(errText(e)));
  useEffect(() => { setRows(null); load(); }, [eventId]); // eslint-disable-line

  const upd = (i, k, v) => setRows(rows.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const del = async (i) => {
    const r = rows[i];
    if (r._new) return setRows(rows.filter((_, idx) => idx !== i));
    try { await api.delete(`/meal-windows/${r.id}`); toast.success(`${r.name} removed`); load(); }
    catch (e) { toast.error(errText(e)); }
  };

  const save = async () => {
    setBusy(true);
    const ops = rows.map((r, i) => {
      const body = { name: r.name, start: r.start, end: r.end, grace_min: Number(r.grace_min), tiers: r.tiers, sort_order: i };
      if (r._new) return api.post(`/events/${eventId}/meal-windows`, body);
      const o = orig[r.id];
      const diff = Object.fromEntries(Object.entries(body).filter(([k, v]) => JSON.stringify(v) !== JSON.stringify(o?.[k])));
      return Object.keys(diff).length ? api.patch(`/meal-windows/${r.id}`, diff) : null;
    }).filter(Boolean);
    if (!ops.length) { setBusy(false); return toast.info("No changes"); }
    const res = await Promise.allSettled(ops);
    const failed = res.filter((x) => x.status === "rejected");
    failed.length ? toast.error(errText(failed[0].reason)) : toast.success(`Meal windows saved · ${ops.length} change${ops.length > 1 ? "s" : ""} logged`);
    setBusy(false); load();
  };

  if (!rows) return <div className="mono-label" data-testid="meals-loading">Loading meal windows…</div>;
  return (
    <Panel className="p-6">
      <div className="flex items-center justify-between">
        <MonoLabel>Meal Windows</MonoLabel>
        <button data-testid="meals-add-btn" onClick={() => setRows([...rows, NEW()])} className="btn-ghost"><Plus size={14} /> Add window</button>
      </div>
      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left mono-label">
              <th className="py-3 pr-3">Name</th><th className="py-3 pr-3">Start</th><th className="py-3 pr-3">End</th>
              <th className="py-3 pr-3">Grace (min)</th><th className="py-3 pr-3">Tiers (comma-sep, blank = all paid)</th><th className="py-3"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.id} className="hairline-t" data-testid={`meal-row-${i}`}>
                <td className="py-2 pr-3"><input data-testid={`meal-name-${i}`} className="input-x !py-2" value={r.name} onChange={(e) => upd(i, "name", e.target.value)} /></td>
                <td className="py-2 pr-3"><input data-testid={`meal-start-${i}`} className="input-x !py-2 !w-24 !font-mono" value={r.start} onChange={(e) => upd(i, "start", e.target.value)} /></td>
                <td className="py-2 pr-3"><input data-testid={`meal-end-${i}`} className="input-x !py-2 !w-24 !font-mono" value={r.end} onChange={(e) => upd(i, "end", e.target.value)} /></td>
                <td className="py-2 pr-3"><input data-testid={`meal-grace-${i}`} className="input-x !py-2 !w-20" value={r.grace_min} onChange={(e) => upd(i, "grace_min", e.target.value)} /></td>
                <td className="py-2 pr-3"><input data-testid={`meal-tiers-${i}`} className="input-x !py-2" value={r.tiers.join(", ")}
                  onChange={(e) => upd(i, "tiers", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} /></td>
                <td className="py-2 text-right"><button data-testid={`meal-del-${i}`} onClick={() => del(i)} className="p-2 hover:text-[var(--red)]"><Trash2 size={14} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button data-testid="meals-save-btn" className="btn-red mt-5" disabled={busy} onClick={save}>
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save configuration
      </button>
    </Panel>
  );
}
